import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogShell,
  DialogShellBody,
  DialogShellFooter,
  DialogShellHeader
} from '@bliks-io/ui';
import { PublishStatusBadge } from './PublishStatusBadge';

interface ContentEditorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  icon: ReactNode;
  title: string;
  subtitle: string;
  /** Null while creating: a new entry has no status yet. */
  published: boolean | null;
  isSaving: boolean;
  /** Why publishing is not possible yet; shown in the footer and blocks publishing. */
  publishBlockedReason?: string;
  onSave: (publish: boolean) => void;
  children: ReactNode;
}

/**
 * The dialog both partner content types are edited in. Nothing reaches
 * partners before the editor saves; the footer names what saving does.
 */
export function ContentEditorDialog({
  open,
  onOpenChange,
  icon,
  title,
  subtitle,
  published,
  isSaving,
  publishBlockedReason,
  onSave,
  children
}: ContentEditorDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogShell size="lg" minHeight={640}>
        <DialogShellHeader
          icon={icon}
          title={title}
          subtitle={subtitle}
          stepper={published !== null && <PublishStatusBadge published={published} />}
          onClose={() => onOpenChange(false)}
        />
        <DialogShellBody>{children}</DialogShellBody>
        <DialogShellFooter
          hint={publishBlockedReason}
          secondary={{
            label: t(
              published ? 'partnerContent.actions.unpublish' : 'partnerContent.actions.saveDraft'
            ),
            onClick: () => onSave(false),
            disabled: isSaving
          }}
          primary={{
            label: t(published ? 'common.Save' : 'partnerContent.actions.publish'),
            onClick: () => onSave(true),
            disabled: isSaving || Boolean(publishBlockedReason),
            loading: isSaving
          }}
        />
      </DialogShell>
    </Dialog>
  );
}
